import Keycloak from 'keycloak-js'

import logger from '@/utils/_legacy/logger'

export const KEYCLOAK_LOGIN_IN_PROGRESS_KEY = 'keycloak-login-in-progress'
const KC_CALLBACK_PREFIX = 'kc-callback-'

interface KeycloakConfig {
  url: string
  realm: string
  clientId: string
}

interface KeycloakInitConfig {
  onLoad: 'check-sso'
  checkLoginIframe: boolean
  pkceMethod: 'S256'
  flow: 'standard'
  enableLogging: boolean
  silentCheckSsoRedirectUri: string
}

/**
 * Manages Keycloak instance, configuration, and state
 */
export class KeycloakStateManager {
  private instance: Keycloak | null = null
  private config: KeycloakConfig | null = null
  private initializationPromise: Promise<void> | null = null

  getInstance(): Keycloak {
    if (!this.instance) {
      const config = this.getConfig()
      this.instance = new Keycloak({
        url: config.url,
        realm: config.realm,
        clientId: config.clientId
      })
    }
    return this.instance
  }

  getConfig(): KeycloakConfig {
    if (this.config) {
      return this.config
    }

    const url = import.meta.env.VITE_KEYCLOAK_URL
    const realm = import.meta.env.VITE_KEYCLOAK_REALM
    const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID

    if (!url || !realm || !clientId) {
      throw new Error('Keycloak configuration is missing. Please check your environment variables.')
    }

    this.config = { url, realm, clientId }
    return this.config
  }

  getInitConfig(checkLoginIframe: boolean): KeycloakInitConfig {
    return {
      onLoad: 'check-sso',
      checkLoginIframe,
      pkceMethod: 'S256',
      flow: 'standard',
      enableLogging: import.meta.env.DEV,
      silentCheckSsoRedirectUri: `${location.origin}/silent-check-sso.html`
    }
  }

  hasKeycloakCallbackParams(): boolean {
    const urlParams = new URLSearchParams(window.location.search)
    const hashParams = new URLSearchParams(window.location.hash.substring(1))

    return (
      urlParams.has('code') ||
      hashParams.has('code') ||
      urlParams.has('state') ||
      hashParams.has('state')
    )
  }

  setLoginInProgress(): void {
    localStorage.setItem(KEYCLOAK_LOGIN_IN_PROGRESS_KEY, 'true')
  }

  clearLoginInProgress(): void {
    localStorage.removeItem(KEYCLOAK_LOGIN_IN_PROGRESS_KEY)
  }

  isLoginInProgress(): boolean {
    return localStorage.getItem(KEYCLOAK_LOGIN_IN_PROGRESS_KEY) === 'true'
  }
  getInitializationPromise(): Promise<void> | null {
    return this.initializationPromise
  }

  setInitializationPromise(promise: Promise<void> | null): void {
    this.initializationPromise = promise
  }

  isActive(): boolean {
    return this.instance !== null
  }

  cleanup(): void {
    this.initializationPromise = null
    this.clearLoginInProgress()
    this.cleanupLocalStorage()

    if (this.instance) {
      try {
        this.instance.clearToken()
      } catch (error) {
        logger.error(error, 'Error clearing Keycloak token during cleanup')
      }
    }

    this.instance = null
  }

  private cleanupLocalStorage(): void {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(KC_CALLBACK_PREFIX) || key.includes('kc-')) {
        localStorage.removeItem(key)
      }
    })
  }
}
